'use client';

import { useFeatureFlags } from '@/hooks/state/feature-flags';
import clsx from 'clsx';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';

type AvatarType = 'avatar-selection' | 'header' | 'members' | 'profile';

type AvatarProps = {
  avatarUrl?: string;
  className?: string;
  onClick?: () => void;
  selected?: boolean;
  type: AvatarType;
  useFallbackImage?: boolean;
};

const fallbackAvatarUrl = '/img/icon_guest.svg';

export const AvatarComponent = ({ avatarUrl, className, onClick, selected, type, useFallbackImage = true }: AvatarProps): React.JSX.Element => {
  const [imgSrc, setImgSrc] = useState(avatarUrl);
  const { data: featureFlags } = useFeatureFlags();

  let ringClasses = '';
  let size = 40;
  let twClass = 'w-6 h-6 md:w-8 md:h-8';

  switch (type) {
    case 'avatar-selection':
      ringClasses = clsx(selected ? 'ring-4 ring-primary' : '',
        'rounded-full overflow-hidden');
      size = 80;
      twClass = 'w-16 h-16 p-[4px]';
      break;
    case 'header':
      ringClasses = clsx(selected && imgSrc && imgSrc !== fallbackAvatarUrl ? 'ring-2 ring-primary' : '',
        'rounded-full overflow-hidden');
      twClass = 'w-6 h-6 md:w-8 md:h-8 p-[2px]';
      break;
    case 'members':
      size = 70;
      twClass = 'w-14 h-14';
      break;
    case 'profile':
      size = 50;
      twClass = 'w-10 h-10';
      break;
    default:
      break;
  }

  const commonClasses = clsx(onClick ? 'cursor-pointer hover:opacity-80' : '');

  useEffect(() => {
    setImgSrc(avatarUrl);
  }, [avatarUrl]);

  if (imgSrc && featureFlags?.enableAvatars) {
    return (
      <div className={clsx(ringClasses,
        className,
        twClass
      )}
      >
        <Image alt="avatar"
          className={commonClasses}
          data-test={`avatar-image-${type}`}
          height={size}
          src={imgSrc}
          width={size}
          onClick={onClick}
          onError={() => useFallbackImage ? setImgSrc(fallbackAvatarUrl) : undefined}
        />
      </div>
    );
  }

  return (
    <Image alt="avatar"
      className={clsx('icon-primary', commonClasses, className, twClass)}
      data-test={`avatar-default-${type}`}
      height={40}
      role="img"
      src={fallbackAvatarUrl}
      width={40}
      onClick={onClick}
    />
  );
};
